"use client";

import React from "react";
import { Accordion, AccordionContent, AccordionItem } from "../ui/accordion";
import { PlusIcon } from "@heroicons/react/24/outline";
import { AnimatedLink } from "../ui/animated-link";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { cn } from "@/lib/utils";

export default function FaqComponent() {
  // todo: should be from the backend
  const faq = [
    {
      question: "What is Onyx Marketplace?",
      answer:
        "Onyx Marketplace is an online shopping platform that lets you buy products easily with flexible payment options. You can choose to pay in full, pay later, or split your payment into installments, making shopping more convenient and affordable. Onyx connects trusted vendors with customers while offering secure payments and transparent pricing.",
    },
    {
      question: "How does Buy Now, Pay Later work on Onyx?",
      answer:
        "Buy Now, Pay Later on Onyx allows you to purchase items immediately and pay for them over time. When you checkout, you can select a payment plan that suits your budget. The amount is split into manageable installments, and you'll receive reminders before each payment is due. This makes it easier to afford the things you need without waiting.",
    },
    {
      question: "Who can use Onyx payment plans?",
      answer:
        "Onyx payment plans are available to registered users who meet our eligibility criteria. You must be at least 18 years old, have a valid email address and phone number, and complete our verification process. We assess each application to ensure responsible lending and a smooth shopping experience for all our customers.",
    },
    {
      question: "Are there interest charges on Onyx payment plans?",
      answer:
        "Onyx offers transparent pricing with no hidden fees. Some payment plans may include interest charges depending on the plan you choose and the duration of your payment schedule. All fees and charges are clearly displayed before you confirm your purchase, so you always know exactly what you'll pay.",
    },
    {
      question: "How do repayments work on Onyx?",
      answer:
        "Repayments on Onyx are simple and automated. Once you choose a payment plan, we'll set up automatic payments from your linked payment method. You'll receive email reminders before each payment is processed. You can also make early payments or pay off your balance in full at any time through your account dashboard.",
    },
    {
      question: "Is my personal and payment information safe on Onyx?",
      answer:
        "Yes, your security is our top priority. Onyx uses industry-standard encryption and security measures to protect your personal and payment information. We comply with data protection regulations and never share your information with third parties without your consent. All transactions are processed through secure payment gateways.",
    },
    {
      question: "How do refunds work on Onyx?",
      answer:
        "If you need to return an item, you can initiate a refund request through your account dashboard or by contacting our support team. Once your return is approved and we receive the item in its original condition, we'll process your refund. If you used a payment plan, the refund will be applied to your remaining balance or original payment method, depending on your situation.",
    },
    {
      question: "Can I shop on Onyx without using credit?",
      answer:
        "Absolutely! Onyx offers multiple payment options. You can pay in full upfront using any accepted payment method, including debit cards, bank transfers, or other payment options. Payment plans are optional and available for those who prefer to spread their payments over time.",
    },
    {
      question: "How long does delivery take on Onyx?",
      answer:
        "Delivery times vary depending on the vendor and your location. Most orders are processed within 1-2 business days and delivered within 3-7 business days. You'll receive a tracking number once your order ships, and you can monitor its progress in real-time through your account dashboard or via email updates.",
    },
  ];

  // Split FAQs into two halves
  const midpoint = Math.ceil(faq.length / 2);
  const leftColumnFaqs = faq.slice(0, midpoint);
  const rightColumnFaqs = faq.slice(midpoint);

  // Custom trigger component with Plus/X icons
  const CustomAccordionTrigger = React.forwardRef<
    React.ElementRef<typeof AccordionPrimitive.Trigger>,
    React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>
  >(({ className, children, ...props }, ref) => (
    <AccordionPrimitive.Header className='flex'>
      <AccordionPrimitive.Trigger
        ref={ref}
        className={cn(
          "[&[data-state=closed]_span]:text-text-title [&[data-state=open]_span]:text-link-primary [&[data-state=open]_svg]:text-link-primary flex flex-1 cursor-pointer items-center justify-between gap-4 px-4 py-4 text-left transition-all duration-200 ease-in-out outline-none hover:no-underline focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&[data-state=open]_span]:text-xl md:[&[data-state=open]_span]:text-2xl lg:[&[data-state=open]_span]:text-3xl [&[data-state=open]_svg]:rotate-45",
          className
        )}
        {...props}
      >
        <span className='text-sm font-medium transition-colors'>{children}</span>
        <PlusIcon className='text-icon-title size-5 shrink-0 transition-transform duration-200' />
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  ));
  CustomAccordionTrigger.displayName = "CustomAccordionTrigger";

  return (
    <div className='mx-auto w-full max-w-screen-2xl px-4 py-10'>
      {/* Two Column Flex Layout */}
      <Accordion type='single' collapsible className='w-full'>
        <div className='mx-auto flex w-full flex-col gap-3 md:flex-row md:justify-center md:gap-5 lg:gap-8'>
          {/* Left Column */}
          <div className='flex w-full flex-col gap-3 md:w-1/2'>
            {leftColumnFaqs.map((item, index) => (
              <AccordionItem
                key={index}
                value={item.question}
                className='data-[state=open]:border-border-title rounded-2xl border-b-0 bg-white data-[state=open]:border-[1.5px] data-[state=open]:bg-transparent dark:bg-[#141414] dark:data-[state=open]:bg-transparent'
              >
                <CustomAccordionTrigger className='w-full'>{item.question}</CustomAccordionTrigger>
                <AccordionContent className='text-text-title/90 mt-2 px-4 pb-4 text-sm leading-relaxed font-normal'>
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </div>

          {/* Right Column */}
          <div className='flex w-full flex-col gap-3 md:w-1/2'>
            {rightColumnFaqs.map((item, index) => (
              <AccordionItem
                key={index}
                value={item.question}
                className='data-[state=open]:border-border-title rounded-2xl border-b-0 bg-white data-[state=open]:border-[1.5px] data-[state=open]:bg-transparent dark:bg-[#141414] dark:data-[state=open]:bg-transparent'
              >
                <CustomAccordionTrigger className='w-full'>{item.question}</CustomAccordionTrigger>
                <AccordionContent className='text-text-title/90 mt-2 px-4 pb-4 text-sm leading-relaxed font-normal'>
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </div>
        </div>
      </Accordion>

      {/* Footer */}
      <div className='mt-12 text-center'>
        <p className='text-text-title text-xs font-semibold md:text-sm'>
          Still Questions?{" "}
          <AnimatedLink
            href='mailto:support@onyxng.com'
            target='_blank'
            showHoverCircle={false}
            className='text-link-primary mx-4.5 underline hover:underline'
          >
            Contact our support team
          </AnimatedLink>
        </p>
      </div>
    </div>
  );
}
